import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Animated, Dimensions } from 'react-native';
import ExpandableToast from './ExpandableToast';
import { NotificationData } from '../services/notification-service';

const { height: screenHeight } = Dimensions.get('window');

interface ToastItem extends NotificationData {
  id: string;
  timestamp: number;
}

interface ExpandableToastManagerProps {
  notification: NotificationData;
}

const ExpandableToastManager: React.FC<ExpandableToastManagerProps> = ({
  notification,
}) => {
  const [activeToasts, setActiveToasts] = useState<ToastItem[]>([]);
  const [toastAnimations, setToastAnimations] = useState<Map<string, Animated.Value>>(new Map());

  useEffect(() => {
    // Only handle expandable notifications
    if (
      notification.message && 
      notification.message.trim() !== '' && 
      notification.type !== 'default' &&
      notification.isExpandable
    ) {
      // console.log('ExpandableToastManager showing expandable toast');
      showToast(notification);
    } else if (notification.message && notification.message.trim() !== '' && notification.type !== 'default') {
      // console.log('ExpandableToastManager ignoring non-expandable notification');
    }
  }, [notification]);

  const showToast = (notificationData: NotificationData) => {
    const toastId = `toast_${Date.now()}_${Math.random()}`;
    const newToast: ToastItem = {
      ...notificationData,
      id: toastId,
      timestamp: Date.now(),
    };

    // Create animation for this toast
    const slideAnim = new Animated.Value(-100);
    
    // Add to active toasts
    setActiveToasts(prev => [...prev, newToast]);
    setToastAnimations(prev => new Map(prev.set(toastId, slideAnim)));

    // Animate in
    Animated.spring(slideAnim, {
      toValue: 0,
      useNativeDriver: true,
      tension: 100,
      friction: 8,
    }).start();

    // Auto-hide after delay (longer for expandable toasts)
    const hideDelay = notificationData.isExpandable ? 6000 : 4000;
    setTimeout(() => {
      hideToast(toastId);
    }, hideDelay);
  };

  const hideToast = (toastId: string) => {
    const slideAnim = toastAnimations.get(toastId);
    if (slideAnim) {
      // Animate out
      Animated.timing(slideAnim, {
        toValue: -100,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        // Remove from state after animation
        setActiveToasts(prev => prev.filter(toast => toast.id !== toastId));
        setToastAnimations(prev => {
          const newMap = new Map(prev);
          newMap.delete(toastId);
          return newMap;
        });
      });
    }
  };

  return (
    <View style={styles.container} pointerEvents="box-none">
      {activeToasts.map((toast, index) => {
        const slideAnim = toastAnimations.get(toast.id);
        return (
          <Animated.View
            key={toast.id}
            style={[
              styles.toastWrapper,
              {
                top: 50 + (index * 10), // Slight offset for multiple toasts
                transform: slideAnim ? [{ translateY: slideAnim }] : [],
                zIndex: 1000 - index, // Stack order
              },
            ]}
            pointerEvents="box-none"
          >
            <ExpandableToast
              type={toast.type as 'success' | 'error' | 'info'}
              title={getToastTitle(toast)}
              message={toast.message}
              fullMessage={toast.fullMessage}
              isExpandable={toast.isExpandable || false}
              onHide={() => hideToast(toast.id)}
            />
          </Animated.View>
        );
      })}
    </View>
  );
};

const getToastTitle = (toast: ToastItem): string => {
  switch (toast.type) {
    case 'success':
      return 'Success';
    case 'error':
      return 'Error';
    case 'info':
      return 'Information';
    default:
      return 'Notification';
  }
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    pointerEvents: 'box-none',
  },
  toastWrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    pointerEvents: 'auto',
  },
});

export default ExpandableToastManager;
